import { getChallengeSdk } from './challengeSdk'

const RAW_BASE_URL = process.env.VUE_APP_API_BASE || process.env.VUE_APP_PROXY_TARGET || ''
const NORMALIZED_BASE_URL = String(RAW_BASE_URL || '').replace(/\/+$/, '')
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : NORMALIZED_BASE_URL
const APP_BASE_PATH = `/${String(process.env.BASE_URL || '/').replace(/^\/+|\/+$/g, '')}`.replace(/\/+/g, '/')
const USE_HASH_ROUTER = process.env.VUE_APP_ROUTER_MODE !== 'history'

function joinBaseUrl(baseUrl, path) {
  if (!baseUrl) return path
  if (baseUrl.endsWith('/') && path.startsWith('/')) return `${baseUrl.slice(0, -1)}${path}`
  if (!baseUrl.endsWith('/') && !path.startsWith('/')) return `${baseUrl}/${path}`
  return `${baseUrl}${path}`
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function normalizeFetchBody(body) {
  if (body == null) return { body: undefined, shouldSetJsonHeader: false }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return { body, shouldSetJsonHeader: false }
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return { body, shouldSetJsonHeader: false }
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return { body, shouldSetJsonHeader: false }
  }
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return { body, shouldSetJsonHeader: false }
  }
  if (typeof body === 'string') {
    return { body, shouldSetJsonHeader: true }
  }
  if (isPlainObject(body) || Array.isArray(body)) {
    return { body: JSON.stringify(body), shouldSetJsonHeader: true }
  }
  return { body, shouldSetJsonHeader: false }
}

function extractErrorMessage(data, status) {
  if (typeof data?.error === 'string') return data.error
  if (typeof data?.message === 'string') return data.message
  if (typeof data?.msg === 'string') return data.msg
  if (typeof data?.detail === 'string') return data.detail
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((item) => item.message ?? item.msg ?? item).filter(Boolean).join(', ')
  }
  if (status === 400) return 'Request parameters are invalid'
  if (status === 401) return 'Login has expired, please sign in again'
  if (status === 500) return 'Internal server error'
  return `Request failed (${status})`
}

function handleUnauthorized() {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('authRole')
  const appBasePath = APP_BASE_PATH === '/' ? '' : APP_BASE_PATH
  const loginPath = USE_HASH_ROUTER ? `${appBasePath}/#/auth/login` : `${appBasePath}/auth/login`
  window.location.href = `${window.location.origin}${loginPath}`
}

function maybeHandleUnauthorizedResponse(response, { skipAuth, skip401Redirect, token }) {
  if (response.status === 401 && !skipAuth && token && !skip401Redirect) {
    handleUnauthorized()
  }
}

export function buildUrl(path, params = {}) {
  const search = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, value)
  })
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

export async function requestRaw(path, options = {}) {
  const { skipAuth, skip401Redirect, baseURL, ...fetchOptions } = options
  const resolvedBaseUrl = typeof baseURL === 'string' ? baseURL : BASE_URL
  const url = path.startsWith('http') ? path : joinBaseUrl(resolvedBaseUrl, path)
  const token = skipAuth ? null : localStorage.getItem('token')
  const method = String(fetchOptions.method || 'GET').toUpperCase()
  const normalizedBody = normalizeFetchBody(fetchOptions.body)
  const shouldAttachJsonHeader = normalizedBody.shouldSetJsonHeader && method !== 'GET' && method !== 'HEAD'
  const headers = {
    ...(shouldAttachJsonHeader ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...fetchOptions.headers
  }

  if (headers['Content-Type'] == null) delete headers['Content-Type']

  const response = await getChallengeSdk().fetch(url, {
    ...fetchOptions,
    method,
    body: normalizedBody.body,
    headers
  })

  maybeHandleUnauthorizedResponse(response, { skipAuth, skip401Redirect, token })

  return response
}

export async function request(path, options = {}) {
  const { skipAuth, skip401Redirect, silent, baseURL, ...fetchOptions } = options
  const token = skipAuth ? null : localStorage.getItem('token')
  const response = await requestRaw(path, {
    skipAuth,
    skip401Redirect,
    baseURL,
    ...fetchOptions
  })
  const rawText = await response.text().catch(() => '')
  let data = {}

  if (rawText) {
    try {
      data = JSON.parse(rawText)
    } catch (_) {
      data = { rawText }
    }
  }

  if (!response.ok) {
    const errMsg = extractErrorMessage(data, response.status)
    if (response.status === 401 && !skipAuth && token && !skip401Redirect) {
      throw new Error(errMsg)
    }
    if (response.status === 500 && !silent) {
      alert(errMsg || 'Server error')
    }
    const err = new Error(errMsg)
    err.status = response.status
    err.data = data
    throw err
  }

  return data
}

export { BASE_URL }
